/**
 * useAgentRunStream — live feed for one Website Studio run.
 *
 * Primary transport: WebSocket to the run's AgentRunRoom (replays everything
 * after the last seen seq on connect, then streams). Fallback: polling the
 * cursor endpoint every 2.5 s if the socket can't be opened. Either way the
 * caller gets an ordered, de-duplicated event list plus the latest run view.
 *
 * Stops on its own once the run reaches a terminal phase.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { studioHelpers, type StudioEvent, type StudioRun } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { getAuthToken } from '@/stores/auth';

const TERMINAL = new Set(['done', 'failed', 'cancelled', 'needs_credits']);
const POLL_MS = 2500;
const MAX_EVENTS = 1500;

export interface AgentRunStream {
  events: StudioEvent[];
  run: StudioRun | null;
  connected: 'ws' | 'poll' | 'idle';
  usedCredits: number;
}

export function useAgentRunStream(
  projectId: string | undefined,
  runId: string | undefined
): AgentRunStream {
  const queryClient = useQueryClient();
  const [events, setEvents] = useState<StudioEvent[]>([]);
  const [run, setRun] = useState<StudioRun | null>(null);
  const [connected, setConnected] = useState<'ws' | 'poll' | 'idle'>('idle');
  const [usedCredits, setUsedCredits] = useState(0);
  const lastSeq = useRef(0);
  const stopped = useRef(false);

  const absorb = useCallback((incoming: StudioEvent[]) => {
    if (!incoming.length) return;
    setEvents((prev) => {
      const seen = new Set(prev.map((e) => e.seq));
      const fresh = incoming.filter((e) => !seen.has(e.seq)).sort((a, b) => a.seq - b.seq);
      if (!fresh.length) return prev;
      const next = prev.concat(fresh);
      return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
    });
    for (const e of incoming) {
      if (e.seq > lastSeq.current) lastSeq.current = e.seq;
      if (e.type === 'credits' && typeof e.data?.usedCredits === 'number') {
        setUsedCredits(e.data.usedCredits as number);
      }
    }
  }, []);

  useEffect(() => {
    if (!projectId || !runId) return;
    stopped.current = false;
    lastSeq.current = 0;
    setEvents([]);
    setRun(null);
    setUsedCredits(0);

    let ws: WebSocket | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const refreshRun = async () => {
      try {
        const r = await studioHelpers.getRun(projectId, runId);
        if (disposed) return;
        setRun(r);
        setUsedCredits((c) => Math.max(c, r.credits.used, r.credits.charged));
        if (TERMINAL.has(r.phase)) {
          stopped.current = true;
          queryClient.invalidateQueries({ queryKey: qk.studio(projectId) });
          queryClient.invalidateQueries({ queryKey: qk.aiCredits() });
          queryClient.invalidateQueries({ queryKey: qk.projectAll(projectId) });
        }
      } catch {
        /* keep last */
      }
    };

    const poll = async () => {
      if (disposed || stopped.current) return;
      try {
        const { events: batch, run: r } = await studioHelpers.events(
          projectId,
          runId,
          lastSeq.current
        );
        if (disposed) return;
        absorb(batch);
        setRun(r);
        setUsedCredits((c) => Math.max(c, r.credits.used, r.credits.charged));
        if (TERMINAL.has(r.phase)) {
          stopped.current = true;
          queryClient.invalidateQueries({ queryKey: qk.studio(projectId) });
          queryClient.invalidateQueries({ queryKey: qk.aiCredits() });
          return;
        }
      } catch {
        /* retry next tick */
      }
      pollTimer = setTimeout(poll, POLL_MS);
    };

    const startPolling = () => {
      if (disposed || stopped.current) return;
      setConnected('poll');
      void poll();
    };

    // Same resolver as the axios interceptor and the chatbot socket: this is
    // the third place a token reaches a WebSocket query param, and hand-rolling
    // it here is how the chatbot's socket ended up reading a key nobody writes.
    const token = getAuthToken() || '';
    // First paint: catch-up + run view, regardless of transport.
    void poll().then(() => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      if (disposed || stopped.current) return;
      try {
        ws = new WebSocket(studioHelpers.streamUrl(projectId, runId, token, lastSeq.current));
      } catch {
        startPolling();
        return;
      }
      let opened = false;
      ws.onopen = () => {
        opened = true;
        setConnected('ws');
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as StudioEvent & { type: string };
          if (msg.type === 'hello' || msg.type === 'pong') return;
          absorb([msg]);
          // Milestones change the run view — refresh it (cheap, cached on the server).
          if (
            msg.type === 'phase' ||
            msg.type.startsWith('run.') ||
            msg.type.startsWith('deploy.') ||
            msg.type === 'commit' ||
            msg.type === 'build.fail'
          ) {
            void refreshRun();
          }
        } catch {
          /* ignore malformed frame */
        }
      };
      ws.onerror = () => {
        if (!opened) startPolling();
      };
      ws.onclose = () => {
        if (disposed || stopped.current) return;
        // Socket dropped mid-run — fall back to polling rather than reconnect storms.
        startPolling();
      };
    });

    const keepalive = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25_000);

    return () => {
      disposed = true;
      clearInterval(keepalive);
      if (pollTimer) clearTimeout(pollTimer);
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
    };
  }, [projectId, runId, absorb, queryClient]);

  return { events, run, connected, usedCredits };
}
